<template>
    <div>
        <div class="mb-2 py-3 flex flex-row items-center text-black dark:text-white">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
            </svg>
            <input
                v-model="search"
                placeholder="Search"
                type="text"
                class="border-0 bg-transparent ml-3 py-2 px-4 flex-1 transition focus:border-b focus:outline-none text-lg"
            />
        </div>
        <div v-if="fullWidth || showSelectAll" class="mb-3">
            <button v-if="!maxItems" @click.prevent="handleSelectAll" type="button" class="mr-2 rounded-md text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-2 focus:outline-none">Select all</button>
            <button @click.prevent="handleDeselectAll" type="button" class="rounded-md text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-2 focus:outline-none">Deselect all</button>
        </div>
        <div v-if="!retrieved" class="w-full text-lg text-gray-300">
             <p class="text-center w-full">Loading...</p>
        </div>
        <div v-if="retrieved && filteredObjects.length === 0 " class="w-full text-lg text-gray-300">
             <p class="text-center w-full" v-if="!emptyMessage">No Results Found!</p>
             <p class="text-center w-full" v-if="emptyMessage">{{ emptyMessage }}</p>
        </div>
        <ul
            v-if="retrieved && filteredObjects.length > 0"
            :class="['w-full', 'space-y', 'border-t', 'border-b', fullWidth ? 'flex flex-wrap' : null]"
        >
            <li
                :dusk="`slideoverListItem-${index}`"
                @click="() => selectObject(object)"
                v-for="(object, index) in filteredObjects"
                :key="`list_item-${index}`"
                :class="['cursor-pointer', 'rounded', 'flex', fullWidth ? null : 'flex-row', 'py-5', 'px-3', 'group', 'hover:bg-gray-200 dark:hover:bg-gray-600 dark:hover:text-gray-100', 'transition', 'duration-150', 'cursor-pointer', 'justify-between']"
            >
                <div class="flex flex-row items-center">
                    <p class="text-gray-700 group-hover:text-gray-800 dark:text-gray-200 dark:group-hover:text-gray-100">
                        {{ getItemLabel(object) }}
                    </p>
                    <span
                        class="text-indigo-500 ml-3 group-hover:text-indigo-800 dark:text-gray-200 dark:group-hover:text-gray-100"
                        v-if="multiple && selected.includes(object.id)"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </span>
                </div>
                <span
                    class="text-gray-300 transition transform group-hover:translate-x-2 group-hover:text-gray-600 dark:group-hover:text-gray-100 duration-150 ease-out"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </span>
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    name: "List",
    props: {
        listRoute: String,
        excludedIds: Array,
        fullWidth: {
            type: Boolean,
            default: false,
        },
        showSelectAll: {
            type: Boolean,
            default: false,
        },
        label: String,
        multiple: Boolean,
        selected: {
            type: Array,
            default: () => [],
        },
        language: Object,
        listData: Array,
        maxItems: Number,
        query: Object,
        order: Object,
        withDocs: Array,
        emptyMessage: {
            type: String,
            required: false
        },
        parent: Object,
        excludedDocuments: Array
    },
    data() {
        return {
            objects: [],
            search: "",
            retrieved: false,
        };
    },
    async created() {
        if (!this.listData) {
            const {data} = await axios.get(this.scopedListRoute).catch((err) => {
                console.error(err.response || err);
            });
            this.objects = data;
        }
        else {
            this.objects = this.listData;
        }
        this.retrieved = true;
        this.$emit("list-loaded", this.objects?.data?.length ?? this.objects?.length);
    },
    computed: {
        filteredObjects() {
            if (!this.search) {
                if(this.objects.data){
                    return this.objects.data;
                }
                return this.objects;
            }
            const objects = this.objects?.data ?? this.objects;
            return objects.filter((object) => {
                return this.getItemLabel(object)
                    .toLowerCase()
                    .includes(this.search.toLowerCase());
            });
        },
        scopedListRoute(){
            let route = this.listRoute;
            let params = {};
            if(this.language && this.language.id) {
                params.language = this.language.id;
                params.languages = this.language.id;
            }
            if(this.parent && this.parent.id && this.parent.type) {
                params.parent_id = this.parent.id;
                params.parent_type = this.parent.type;
            }
            if(this.excludedIds && this.excludedIds?.length > 0) {
                params.excluded = (this.excludedIds || []).join(',');
            }

            if(this.excludedDocuments && this.excludedDocuments?.length > 0) {
                params.excludedDocs = this.excludedDocuments.map(doc => JSON.stringify(doc));
            }

            if(this.withDocs && this.withDocs?.length > 0) {
                params.with = this.withDocs;
            }
            if(this.query) {
                params.query = JSON.stringify(this.query);
            }
            if(this.order){
                params.order = JSON.stringify(this.order);
            }

            let queryString =
                (route.includes('?') ? '&' : '?')
                    + this.createQueryString(params);

            return route + queryString;
        }
    },
    methods: {
        createQueryString(params) {
            return Object.entries(params).map(([key, value]) => {
                if (Array.isArray(value)) {
                    return value.map(val => `${key}[]=${val}`).join('&');
                } else {
                    return `${key}=${value}`;
                }
            }).join('&');
        },
        handleSelectAll() {
            this.$emit('select-all', this.objects?.data ?? this.objects);
        },
        handleDeselectAll() {
            this.$emit('deselect-all');
        },
        getItemLabel(object){
            let columnSelector = this.label || "name"
            let label = object[columnSelector]
            if(columnSelector.includes('.')){
                const relationships = columnSelector.split('.')
                let newObject = object
                for(let i = 0; i < relationships.length; i++){
                    console.log(newObject, relationships[i], columnSelector)
                    if(!newObject){
                        break;
                    }
                    newObject = newObject[relationships[i]]
                }
                label = newObject
            }
            return label
        }
    }
};
</script>
